import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { formatInstant, formatLocalDateTime, reminderErrorLabel } from '../utils/collectionReminders';

// Piezas de presentación de una fila de `dispatches`, compartidas por el Historial de la
// pantalla Cobranza y el historial de recordatorios del detalle de nota.

/** Fecha principal de una fila y, si aplica, cuándo está previsto que salga. */
export function useDispatchDates(timeZone) {
  const { t } = useTranslation();
  return (d) => {
    const main = formatInstant(d.sentAt ?? d.createdAt, timeZone) || '—';
    let sub = null;
    if (d.status === 'scheduled') {
      const at = formatLocalDateTime(d.sendAtLocal) || formatInstant(d.notBefore, timeZone);
      if (at) sub = t('Sale el {{fecha}}', { fecha: at });
    } else if (d.status === 'retryable' && d.nextAttemptAt) {
      sub = t('Próximo intento: {{fecha}}', { fecha: formatInstant(d.nextAttemptAt, timeZone) });
    }
    return { main, sub };
  };
}

/**
 * ¿La fila tiene un texto guardado para ver? Lo dice billing (`messageAvailable`: GET
 * messages/:ref daría 200). Tener `messageRef` no alcanza: se asigna al tomar la fila para
 * enviar, y una fila omitida o fallida antes de armar el mensaje lo conserva sin texto.
 */
export const hasViewableMessage = (dispatch) =>
  Boolean(dispatch?.messageRef) && dispatch?.messageAvailable === true;

/** Botón "Ver mensaje" de una fila: abre el texto guardado del mensaje (GET messages/:ref). */
export function ViewMessageButton({ onClick }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded text-xs font-medium text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <Eye className="h-3.5 w-3.5" aria-hidden />
      {t('Ver mensaje')}
    </button>
  );
}

/**
 * Motivo legible de una fila: el errorCode traducido y los intentos si hubo más de uno. Con
 * `onViewMessage`, suma "Ver mensaje" solo si billing tiene el texto de esa fila
 * (`hasViewableMessage`).
 *
 * @param {{ dispatch: object, onViewMessage?: (dispatch: object) => void }} props
 */
export function DispatchDetail({ dispatch, onViewMessage }) {
  const { t } = useTranslation();
  const label = reminderErrorLabel(dispatch.errorCode);
  const reason = dispatch.errorCode
    ? label
      ? t(label)
      : t('Error no reconocido ({{code}})', { code: dispatch.errorCode })
    : null;
  const viewable = Boolean(onViewMessage) && hasViewableMessage(dispatch);
  return (
    <span className="block">
      {reason ?? (!viewable && <span className="bill-td-sub">—</span>)}
      {Number(dispatch.attempts) > 1 && (
        <span className="bill-td-sub block">{t('Intentos: {{n}}', { n: dispatch.attempts })}</span>
      )}
      {viewable && (
        <span className="block">
          <ViewMessageButton onClick={() => onViewMessage(dispatch)} />
        </span>
      )}
    </span>
  );
}
